import { AccountBalance, Groups3, Inventory, PointOfSale, Storage } from '@mui/icons-material';
import { Grid2, Tab, TabProps, Tabs, useMediaQuery } from '@mui/material';

import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { translations } from 'locales/i18n';
import theme from 'styles/theme/theme';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { ArticleTab } from './components/Article/Article';
import { DepartmentSettings } from './components/Department/Department';
import { MetadataTab } from './components/Metadata/Metadata';
import { RevenueTypeSettings } from './components/RevenueType/RevenueType';
import { TaxTab } from './components/Tax/Tax';

enum Settings {
  TAX = 'tax',
  METADATA = 'metadata',
  REVENUETYPE = 'revenuetype',
  ARTICLE = 'article',
  DEPARTMENT = 'department'
}

const settingsTabs: { value: Settings; icon: JSX.Element; style?: CSSProperties }[] = [
  { value: Settings.TAX, icon: <AccountBalance />, style: { borderTopLeftRadius: 12 } },
  { value: Settings.REVENUETYPE, icon: <PointOfSale /> },
  { value: Settings.METADATA, icon: <Storage /> },
  { value: Settings.ARTICLE, icon: <Inventory /> },
  { value: Settings.DEPARTMENT, icon: <Groups3 />, style: { borderBottomLeftRadius: 12 } }
];

export const BillingSettings = () => {
  const { t } = useTranslation();
  const biggerThanXS = useMediaQuery(theme('de').breakpoints.up('md'));
  usePageHeading(t(translations.pages.billingSettings.title()));
  const { setSearch, setDebouncedSearchText } = useTableQuery();

  const [activeTab, setActiveTab] = useQueryStringState<Settings>('tab', Settings.TAX);

  const SettingsTab = (props: TabProps) => (
    <Tab
      {...props}
      style={{
        padding: '0em 1em',
        border: '0.5px solid #ddd',
        justifyContent: biggerThanXS ? 'flex-start' : 'center',
        minHeight: '4.5em',
        ...props.style
      }}
      iconPosition="start"
    />
  );

  const renderSettingsTab = () => {
    switch (activeTab) {
      case Settings.TAX:
        return <TaxTab />;
      case Settings.REVENUETYPE:
        return <RevenueTypeSettings />;
      case Settings.METADATA:
        return <MetadataTab />;
      case Settings.ARTICLE:
        return <ArticleTab />;
      case Settings.DEPARTMENT:
        return <DepartmentSettings />;
    }
  };

  return (
    <Grid2 container gap={2} direction="row">
      <Grid2 size={2} paddingTop={'2.78125em'}>
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => {
            setActiveTab(newVal);
            setSearch('');
            setDebouncedSearchText('');
          }}
          orientation="vertical"
          aria-label="vertical tabs example"
        >
          {settingsTabs.map(settings => (
            <SettingsTab
              key={settings.value}
              value={settings.value}
              icon={settings.icon}
              label={
                biggerThanXS
                  ? t(translations.pages.billingSettings.tabs[settings.value]())
                  : undefined
              }
              style={settings.style}
            />
          ))}
        </Tabs>
      </Grid2>
      <Grid2 size={'grow'}>{renderSettingsTab()}</Grid2>
    </Grid2>
  );
};
